import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  useDisclosure, Flex, Text, Box, HStack, Select, Input, Tag,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import {
  ImmAgenda, listarAgenda, eliminarAgenda,
} from '../../../api/immApi';
import { fmt, IMM_COLOR, IMM_COLOR_HOVER, EstatusBadge } from '../_shared';
import FormularioAgenda from './FormularioAgenda';

const TIPOS = ['Reunion', 'Oficio', 'Invitacion', 'Conferencia', 'Evento', 'Cita'];

const TablaAgenda: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const verD = useDisclosure();
  const editarD = useDisclosure();
  const nuevoD = useDisclosure();
  const bajaD = useDisclosure();

  const [data, setData] = useState<ImmAgenda[]>([]);
  const [selected, setSelected] = useState<ImmAgenda | null>(null);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setCargando(true);
    try {
      setData(await listarAgenda());
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const datosFiltrados = data.filter((item) => {
    const matchTipo = filtroTipo ? item.tipo === filtroTipo : true;
    const matchEstatus = filtroEstatus ? item.estatus === filtroEstatus : true;
    const q = busqueda.toLowerCase();
    const matchBusqueda = q
      ? item.tema?.toLowerCase().includes(q) ||
        item.lugar?.toLowerCase().includes(q)
      : true;
    return matchTipo && matchEstatus && matchBusqueda;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const estatusOpciones = Array.from(new Set(data.map((d) => d.estatus).filter(Boolean)));

  const handleGuardado = () => { nuevoD.onClose(); editarD.onClose(); fetchData(); };

  const handleBajaConfirmar = async () => {
    if (!selected) return;
    setProcesando(true);
    try {
      await eliminarAgenda(selected.id);
      toast({ title: 'Registro eliminado', status: 'success', duration: 3000 });
      bajaD.onClose();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo eliminar el registro.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleExportExcel = () => {
    const filas = datosFiltrados.map((item) => ({
      'Fecha': fmt(item.fecha),
      'Hora': item.hora || '',
      'Tipo': item.tipo,
      'Tema': item.tema || '',
      'Lugar': item.lugar || '',
      'Estatus': item.estatus,
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Agenda IMM');
    XLSX.writeFile(wb, `Agenda_IMM_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input placeholder="Buscar por tema o lugar..." value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }} size="sm" w={{ base: 'full', md: '260px' }} />
          <Select placeholder="Tipo" value={filtroTipo} onChange={(e) => { setFiltroTipo(e.target.value); setCurrentPage(1); }} size="sm" w="160px">
            {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
          <Select placeholder="Estatus" value={filtroEstatus} onChange={(e) => { setFiltroEstatus(e.target.value); setCurrentPage(1); }} size="sm" w="150px">
            {estatusOpciones.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={nuevoD.onOpen}>Nuevo registro</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg={IMM_COLOR}>
            <Tr>
              <Th color="white" fontSize="xs">Fecha</Th>
              <Th color="white" fontSize="xs">Hora</Th>
              <Th color="white" fontSize="xs">Tipo</Th>
              <Th color="white" fontSize="xs">Tema</Th>
              <Th color="white" fontSize="xs">Lugar</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={7} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={7} textAlign="center" py={8}><Text color="gray.400">No se encontraron registros</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td>{fmt(item.fecha)}</Td>
                  <Td>{item.hora || '—'}</Td>
                  <Td><Tag size="sm" colorScheme="red" variant="subtle">{item.tipo}</Tag></Td>
                  <Td><Text noOfLines={1} fontSize="sm">{item.tema || '—'}</Text></Td>
                  <Td><Text noOfLines={1} fontSize="sm">{item.lugar || '—'}</Text></Td>
                  <Td><EstatusBadge value={item.estatus} /></Td>
                  <Td>
                    <HStack spacing={1}>
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => { setSelected(item); verD.onOpen(); }}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => { setSelected(item); editarD.onOpen(); }}>Editar</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => { setSelected(item); bajaD.onOpen(); }}>Eliminar</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}</Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button key={page} size="xs" bg={currentPage === page ? IMM_COLOR : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>{page}</Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      {/* Ver */}
      <Modal isOpen={verD.isOpen} onClose={verD.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Registro de agenda</ModalHeader>
          <ModalBody py={5}>{selected && <FormularioAgenda modo="view" data={selected} onCancelar={verD.onClose} />}</ModalBody>
        </ModalContent>
      </Modal>

      {/* Editar */}
      <Modal isOpen={editarD.isOpen} onClose={editarD.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Editar registro</ModalHeader>
          <ModalBody py={5}>{selected && <FormularioAgenda modo="edit" data={selected} onGuardado={handleGuardado} onCancelar={editarD.onClose} />}</ModalBody>
        </ModalContent>
      </Modal>

      {/* Nuevo */}
      <Modal isOpen={nuevoD.isOpen} onClose={nuevoD.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Nuevo registro</ModalHeader>
          <ModalBody py={5}><FormularioAgenda modo="new" onGuardado={handleGuardado} onCancelar={nuevoD.onClose} /></ModalBody>
        </ModalContent>
      </Modal>

      {/* Eliminar (físico) */}
      <AlertDialog isOpen={bajaD.isOpen} leastDestructiveRef={cancelRef} onClose={bajaD.onClose}>
        <AlertDialogOverlay><AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" color={IMM_COLOR}>Eliminar registro</AlertDialogHeader>
          <AlertDialogBody>
            El registro de agenda del <strong>{fmt(selected?.fecha)}</strong> se eliminará de forma permanente. ¿Continuar?
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={bajaD.onClose} isDisabled={procesando}>Cancelar</Button>
            <Button colorScheme="red" onClick={handleBajaConfirmar} ml={3} isLoading={procesando} loadingText="Eliminando...">Eliminar</Button>
          </AlertDialogFooter>
        </AlertDialogContent></AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaAgenda;
